import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import {
  getStoredConsent,
  isAdminPath,
  needsConsentDecision,
  OPEN_COOKIE_SETTINGS_EVENT,
  saveConsent
} from '../../utils/analyticsConsent';
import { COLORS, FONTS } from '../../theme/tokens';
import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from '../../theme/v2Styles';

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isAdminRoute = isAdminPath(location.pathname);
  const [visible, setVisible] = React.useState(() => !isAdminRoute && needsConsentDecision());
  const [customizing, setCustomizing] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(false);
  const [marketing, setMarketing] = React.useState(false);

  React.useEffect(() => {
    if (isAdminRoute) {
      setVisible(false);
      return;
    }

    if (needsConsentDecision()) {
      setVisible(true);
    }
  }, [isAdminRoute]);

  React.useEffect(() => {
    const openSettings = () => {
      if (!isAdminPath()) {
        const storedConsent = getStoredConsent();
        setAnalytics(Boolean(storedConsent?.analytics));
        setMarketing(Boolean(storedConsent?.marketing));
        setCustomizing(true);
        setVisible(true);
      }
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    };
  }, []);

  const commitConsent = (nextConsent) => {
    saveConsent(nextConsent);
    setVisible(false);
    setCustomizing(false);
  };

  if (!visible || isAdminRoute) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      role="region"
      aria-label={t('cookies.ariaLabel')}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
        borderRadius: 0,
        borderTop: `1px solid ${COLORS.gold}`,
        bgcolor: COLORS.footerBg,
        color: COLORS.footerText,
        boxShadow: '0 -8px 28px rgba(35,48,42,0.14)'
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          pl: { xs: 2, sm: 3 },
          pr: { xs: 10, sm: 12 },
          py: 2
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h2" variant="h5" sx={{ mb: 0.75, fontFamily: FONTS.heading }}>
              {t('cookies.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(244,241,234,0.78)', lineHeight: 1.6 }}>
              {t('cookies.body')}{' '}
              <Link component={RouterLink} to="/privacy-policy" sx={{ color: COLORS.goldLight }}>
                {t('cookies.privacyLink')}
              </Link>
            </Typography>

            {customizing && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={analytics}
                      onChange={(event) => setAnalytics(event.target.checked)}
                    />
                  }
                  label={t('cookies.analytics')}
                  sx={{ '& .MuiFormControlLabel-label': { color: COLORS.footerText } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={marketing}
                      onChange={(event) => setMarketing(event.target.checked)}
                    />
                  }
                  label={t('cookies.marketing')}
                  sx={{ '& .MuiFormControlLabel-label': { color: COLORS.footerText } }}
                />
              </Stack>
            )}
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ flexShrink: 0 }}
          >
            {customizing ? (
              <Button
                variant="contained"
                onClick={() => commitConsent({ analytics, marketing })}
                sx={PRIMARY_BUTTON_SX}
              >
                {t('cookies.saveChoice')}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  onClick={() => commitConsent({ analytics: true, marketing: true })}
                  sx={PRIMARY_BUTTON_SX}
                >
                  {t('cookies.acceptAll')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => commitConsent({ analytics: false, marketing: false })}
                  sx={{ ...SECONDARY_BUTTON_SX, borderColor: COLORS.footerText, color: COLORS.footerText, '&:hover': { borderColor: COLORS.footerText, bgcolor: 'rgba(255,255,255,0.08)' } }}
                >
                  {t('cookies.essentialOnly')}
                </Button>
                <Button variant="text" onClick={() => setCustomizing(true)} sx={{ color: COLORS.goldLight }}>
                  {t('cookies.customise')}
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default CookieConsentBanner;
